import { type FormEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Footer } from "../components/Footer";
import { usePlatformAuth } from "../context/platform-auth";

const SLUG_REGEX = /^[a-z0-9][a-z0-9-]{0,62}[a-z0-9]$/;
const RESERVED_SLUGS = new Set(["admin"]);

function deriveSlug(name: string): string {
	return name
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "");
}

export function CreateWorkspacePage() {
	const { user } = usePlatformAuth();
	const navigate = useNavigate();
	const [name, setName] = useState("");
	const [slug, setSlug] = useState("");
	const [slugTouched, setSlugTouched] = useState(false);
	const [error, setError] = useState("");
	const [slugError, setSlugError] = useState("");
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (!slugTouched) {
			setSlug(deriveSlug(name));
		}
	}, [name, slugTouched]);

	useEffect(() => {
		if (!slug) {
			setSlugError("");
			return;
		}
		if (RESERVED_SLUGS.has(slug)) {
			setSlugError("This slug is reserved");
		} else if (!SLUG_REGEX.test(slug)) {
			setSlugError(
				"2-64 characters, lowercase letters, numbers, and hyphens only",
			);
		} else {
			setSlugError("");
		}
	}, [slug]);

	const isValid = name.trim().length > 0 && slug.length > 0 && !slugError;

	async function handleSubmit(e: FormEvent) {
		e.preventDefault();
		if (!isValid) return;
		setError("");
		setLoading(true);
		try {
			const res = await fetch("/api/workspaces", {
				method: "POST",
				credentials: "include",
				headers: {
					"Content-Type": "application/json",
					Origin: window.location.origin,
				},
				body: JSON.stringify({ name: name.trim(), slug }),
			});
			if (!res.ok) {
				const body = await res.json().catch(() => null);
				const msg =
					(body as { error?: string } | null)?.error ??
					`Failed to create workspace (${res.status})`;
				setError(msg);
				return;
			}
			const data = (await res.json()) as { slug: string };
			navigate(`/${data.slug}/`, { replace: true });
		} catch {
			setError("Network error");
		} finally {
			setLoading(false);
		}
	}

	return (
		<div className="min-h-screen flex flex-col bg-gray-50">
			<header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
				<a href="/workspaces" className="text-lg font-semibold text-gray-900">
					Apsu
				</a>
				<span className="text-sm text-gray-600">{user?.email}</span>
			</header>
			<main className="flex-1 flex items-center justify-center">
				<div className="w-full max-w-md bg-white rounded-lg shadow px-6 py-8">
					<h1 className="text-2xl font-bold text-gray-900 mb-6">
						Create workspace
					</h1>
					{error && <p className="text-red-600 text-sm mb-4">{error}</p>}
					<form onSubmit={handleSubmit} className="space-y-4">
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">
								Workspace name
								<input
									name="name"
									type="text"
									value={name}
									onChange={(e) => setName(e.target.value)}
									required
									className="mt-1 block w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
								/>
							</label>
						</div>
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">
								Slug
								<input
									name="slug"
									type="text"
									value={slug}
									onChange={(e) => {
										setSlugTouched(true);
										setSlug(e.target.value);
									}}
									required
									className="mt-1 block w-full rounded border border-gray-300 px-3 py-2 text-sm font-mono focus:border-blue-500 focus:outline-none"
								/>
							</label>
							{slugError && (
								<p className="text-red-600 text-xs mt-1">{slugError}</p>
							)}
							{!slugError && slug && (
								<p className="text-gray-500 text-xs mt-1">
									Your workspace will be at /{slug}/
								</p>
							)}
						</div>
						<button
							type="submit"
							disabled={loading || !isValid}
							className="w-full rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
						>
							{loading ? "Creating\u2026" : "Create workspace"}
						</button>
					</form>
				</div>
			</main>
			<Footer />
		</div>
	);
}
